import { observable } from 'mobx'
import { APPConfig } from 'config/appConfig'
import { CommonContainer, cartContainer, customerContainer } from 'src/models'
import { normalAndSpecialProductSkuType } from 'src/views/components/CatalogViews'
import { toastState } from 'src/views/components/Toast'
import { i18nTranslate } from 'src/utils'
import {
  formatDate,
  enableTimeZoneDateConversion,
} from 'src/utils/multipleSubscriptionUtils'

class MultipleSubscriptionContainer extends CommonContainer {
  @observable subscriptionOption = 'startNewSubscription'
  @observable isToNavigateToCheckout = false
  @observable isToShowSelectSubscriptionModal = false
  @observable isToShowAddToExistingSubscriptionModal = false
  @observable isToShowSubscriptionConfirmedModal = false
  @observable selectedSubscriptionOption = ''
  @observable subscriptionGroupName = ''
  @observable isSubscriptionNameValid = false
  @observable allSubscriptionListResponse = {}
  @observable selectedSubscriptionGroupDetails = {}
  @observable isAddToSubscriptionApiInProgress = false
  @observable isToForceUpdateFrequencyInCartUI = false
  @observable subscriptionAddedResponse = {}
  @observable selectedSubscriptionGroupItemResponse = {}

  isUserHasExistingSubscription = () => {
    return cartContainer.subscriptionResponse?.isSubscriptionPreference || false
  }

  getAlreadySubscribedItemsCount = () => {
    return (
      cartContainer.subscriptionResponse
        ?.totalActiveSubscriptionItemQuantityCount || 0
    )
  }

  getSubscriptionItemsInCart = () => {
    let cartItems = cartContainer.cartResponse?.items || []
    let subscriptionItems = cartItems?.filter?.(item => item?.isSubscription)
    return subscriptionItems
  }

  getSubscriptionItemsTotal = () => {
    const cartSubscriptionItems = this.getSubscriptionItemsInCart() || []
    let subscriptionItemsTotal = 0
    cartSubscriptionItems?.map(item => {
      if (item?.totalValue?.priceAfterDiscount) {
        subscriptionItemsTotal =
          subscriptionItemsTotal + item?.totalValue?.priceAfterDiscount
      }
    })
    return subscriptionItemsTotal
  }

  createFilterParamsForAllSubscriptionsList = () => {
    const filterData = {
      filters: [{ field: 'status', operation: 'IN', value: 'ACTIVE,PENDING' }],
    }
    const subscribedItemsLength = this.getAlreadySubscribedItemsCount()

    return {
      filter: JSON.stringify(filterData),
      size: subscribedItemsLength,
      page: 1,
    }
  }

  getAllSubscriptionsList = async () => {
    // let filterParams = this.createFilterParamsForAllSubscriptionsList()

    const loadParams = {
      endPointName: 'getAllMultipleSubscriptions',
      pathParams: '',
      queryParams: {
        size: 50,
        page: 1,
        isLiveAddressRequired: true,
        isLivePriceRequired: true,
        isLivePaymentRequired: true,
        isLiveCatalogRequired: true,
        showAllSubscription: 'false',
      },
    }

    const response = await this.fetchResponse(loadParams)
    if (this.isSuccessResponse(response)) {
      this.allSubscriptionListResponse = response
    } else {
      this.allSubscriptionListResponse = {}
    }
  }

  getDefaultProductSku = productDetails => {
    return (
      productDetails?.skus?.filter?.(sku => {
        const { type = '' } = sku || {}
        const skuType = type?.toLowerCase()?.replace('_', '') || ''
        return normalAndSpecialProductSkuType?.includes?.(skuType)
      }) || []
    )
  }

  getAvailableSubscriptionGroupLists = (subscriptionList = []) => {
    if (subscriptionList?.length) {
      return subscriptionList?.filter(subscriptionGroup => {
        const isSubscriptionGroupAvailable =
          this.checkIfProcessingDaySameAsCurrentDay(subscriptionGroup) || false
        return !isSubscriptionGroupAvailable
      })
    }
    return []
  }

  checkIfProcessingDaySameAsCurrentDay = (subscriptionGroup = {}) => {
    const subscriptionProcessingDay =
      subscriptionGroup?.recurrenceOrderProcessingDate || 1
    const currentDay = new Date()
    const currentDate = currentDay?.getDate() || 1
    return currentDate === subscriptionProcessingDay
  }

  checkIsSomeCartItemAlreadyInSubscribedGroup = (subscriptionGroup = {}) => {
    let subscribedProductsList = subscriptionGroup?.itemInfo || []
    let subscriptionItemsInCart = this.getSubscriptionItemsInCart()
    let isCartItemAlreadySubscribed = subscribedProductsList?.some(
      subscribedItem => {
        let mainSubscribedProductData =
          this.getDefaultProductSku(subscribedItem)
        let subscribedSkuId = mainSubscribedProductData?.[0]?.id || ''
        return subscriptionItemsInCart?.some(cartItem => {
          let mainCartSubscriptionSku = this.getDefaultProductSku(cartItem)
          let cartSkuId = mainCartSubscriptionSku?.[0]?.skuId || ''
          return subscribedSkuId === cartSkuId
        })
      }
    )
    return isCartItemAlreadySubscribed
  }

  checkIsCartItemAlreadyInSubscribedGroup = (
    subscriptionGroup = {},
    cartItemId = {}
  ) => {
    let subscribedProductsList = subscriptionGroup?.itemInfo || []
    let isCartItemAlreadySubscribed = subscribedProductsList?.some(
      subscribedItem => {
        let mainSubscribedProductData =
          this.getDefaultProductSku(subscribedItem)
        let subscribedSkuId = mainSubscribedProductData?.[0]?.id || ''
        let cartSkuId = mainCartSubscriptionSku?.[0]?.skuId || ''
        return subscribedSkuId === cartSkuId
      }
    )
    return isCartItemAlreadySubscribed
  }

  updateItemQuantityInCart = async props => {
    const { quantity = 0, cartItemId = 0 } = props
    const loadParams = {
      endPointName: 'updateCartItem',
      pathParams: cartItemId,
      postData: { quantity },
    }

    const response = await this.fetchResponse(loadParams)
    if (this.isSuccessResponse(response)) {
      await cartContainer.viewCart()
    }
    return response
  }

  updateItemFrequencyInCart = async props => {
    const { cartItemId = 0, subscription = {} } = props

    const loadParams = {
      endPointName: 'updateCartItem',
      pathParams: cartItemId,
      postData: { ...subscription },
    }

    const response = await this.fetchResponse(loadParams)
    if (this.isSuccessResponse(response)) {
      await cartContainer.viewCart()
      this.isToForceUpdateFrequencyInCartUI = true
    }
    return response
  }

  updateSubscriptionGroupName = async () => {
    let enableTimeZoneDateFormat = enableTimeZoneDateConversion() || false
    let todayDate = enableTimeZoneDateFormat
      ? formatDate(Date.now(), { day: '2-digit' })
      : new Date()?.getDate() || 1
    const blackOutDayProcessingDate =
      APPConfig?.getAppConfig()?.defaultProcessingDate || {}
    let processingDate = todayDate
    if (blackOutDayProcessingDate?.[todayDate]) {
      processingDate = blackOutDayProcessingDate[todayDate] || ''
    }

    const payload = {
      subscription: {
        name: this.subscriptionGroupName,
        orderProcessingDate: Number(processingDate),
        frequencyType: 'MONTHS',
        frequencyPeriod: 1,
        skipSubscriptionItems: false,
      },
    }

    const loadParams = {
      endPointName: 'updateBagAttributes',
      postData: payload,
    }

    const response = await this.fetchResponse(loadParams)
    if (this.isSuccessResponse(response)) {
      await cartContainer.viewCart()
    }
    return response
  }

  getSelectedSubscription = selectedSubscriptionGroup => {
    const selectedSubscriptionDetails =
      this.allSubscriptionListResponse?.subscriptions?.find?.(
        subscription => subscription?.id === selectedSubscriptionGroup
      )
    return selectedSubscriptionDetails
  }

  getSelectedSubscriptionDetails = selectedSubscriptionGroup => {
    const selectedSubscriptionDetails = this.getSelectedSubscription(
      selectedSubscriptionGroup
    )

    const selectedSubscription = {
      id: selectedSubscriptionDetails?.id || '',
      name: selectedSubscriptionDetails?.name || '',
      processingDay:
        selectedSubscriptionDetails?.recurrenceOrderProcessingDate || '',
      lastOrderProcessedDate:
        selectedSubscriptionDetails?.lastOrderProcessedDate || '',
      nextOrderCreationDate:
        selectedSubscriptionDetails?.nextOrderCreationDate || '',
    }

    this.selectedSubscriptionGroupDetails = selectedSubscription
  }

  getProductSkuDetails = (skusList = []) => {
    return skusList?.map?.(sku => {
      const { skuId = '', productId = '', type = '' } = sku || {}
      return { id: skuId, skuId, productId, type }
    })
  }

  getTransformedCartProducts = (subscriptionItemId, cartItems = {}) => {
    let transformedCartProducts = cartItems?.map((cartItem, key) => {
      let {
        quantity = 1,
        subscription = {},
        properties = {},
        skus = [],
        itemId = '',
      } = cartItem || {}
      const { category, categoryId, scanCardDetails = '' } = properties || {}
      let mainProductData = this.getProductSkuDetails(skus) || []

      return {
        quantity,
        itemId,
        skus: mainProductData,
        isSubscription: true,
        subscription,
        properties: {
          categoryId,
          category,
          isOneTimeOrderItem: false,
          ...subscription,
          scanCardDetails,
        },
      }
    })
    return transformedCartProducts
  }

  updateCartItemsToSubscription = async props => {
    this.isAddToSubscriptionApiInProgress = true
    const { subscriptionItemId = '', cartItemDetails = {} } = props
    const transformedCartProducts = this.getTransformedCartProducts(
      subscriptionItemId,
      cartItemDetails
    )
    const queryParams = {
      userInfo: customerContainer.userId,
      isDeleteFromCart: true,
    }

    const cartType = cartContainer?.cartResponse?.cartType
    if (cartType) {
      queryParams.cartType = cartType
    }

    const loadParams = {
      endPointName: 'addToSubscription',
      pathParams: `${subscriptionItemId}/item`,
      queryParams,
      postData: transformedCartProducts,
    }

    const response = await this.fetchResponse(loadParams)
    if (this.isSuccessResponse(response)) {
      this.subscriptionAddedResponse = response
      this.isToShowSubscriptionConfirmedModal = true
      await cartContainer.viewCart()
      await multipleSubscriptionContainer.getAllSubscriptionsList()
    }
    this.isAddToSubscriptionApiInProgress = false
    return response
  }

  deletePaymentById = async (paymentId = {}) => {
    const loadParams = {
      endPointName: 'deletePayment',
      pathParams: paymentId,
    }

    const response = await this.fetchResponse(loadParams)
    if (this.isSuccessResponse(response)) {
      toastState.setToastMessage(
        i18nTranslate('payment.deleteSuccess', 'Payment removed successfully'),
        true
      )
    } else if (response?.code === 'EO0003') {
      toastState.setToastMessage(
        i18nTranslate(
          'payment.cannotDeletePayment',
          'Cannot delete this payment as it is already associated with subscription. Kindly update your subscription with a new payment and then proceed with removal.'
        )
      )
    } else {
      toastState.setToastMessage(
        response?.message
          ? response?.message
          : i18nTranslate(
              'payment.deleteFailure',
              'Unable to remove payment, please try again'
            ),
        false
      )
    }
    return response
  }
  quickAddToSubscription = async props => {
    const { subscriptionId = '', data = {}, successCallBack } = props
    const queryParams = { userInfo: customerContainer.userId }

    const cartType = cartContainer?.cartResponse?.cartType
    if (cartType) {
      queryParams.cartType = cartType
    }

    const apiLoadParams = {
      endPointName: 'addToSubscription',
      pathParams: `${subscriptionId}/item`,
      queryParams,
      postData: data,
    }

    const response = await this.fetchResponse(apiLoadParams)
    if (this.isSuccessResponse(response)) {
      await successCallBack()
    }
    return response
  }

  deleteSubscriptionOverrideCartItems = async () => {
    const loadParams = {
      endPointName: 'deleteCartItem',
    }

    const response = await this.fetchResponse(loadParams)

    if (this.isSuccessResponse(response)) {
      toastState.setToastMessage(
        i18nTranslate(
          'cart.removeCartSuccess',
          'Removed from cart successfully'
        ),
        true
      )
    } else {
      const unProcessableRequestMessage = i18nTranslate(
        'cart.unableToProcessMessage',
        'We are currently unable to process your request or it may already be processed. Please refresh your screen and try again'
      )
      const errorMessage =
        response?.code === 'EOCACG0001'
          ? unProcessableRequestMessage
          : response?.message ||
            i18nTranslate(
              'cart.removeCartFailure',
              'Unable to remove product from cart, please try again'
            )
      toastState.setToastMessage(errorMessage, false)
    }

    return response
  }

  updateSubscriptionDataToCart = async (
    subscriptionName = '',
    subscriptionId = '',
    promoCode = ''
  ) => {
    const properties = { subscriptionId, subscriptionName }
    if (promoCode) {
      properties.promocode = promoCode
    }
    const loadParams = {
      endPointName: 'updateBagAttributes',
      postData: { properties },
      cartType: 'SUBSCRIPTION_OVERRIDE',
    }
    const response = await this.fetchResponse(loadParams)
    return response
  }

  applyPromoToOverrideCart = async (promoCode = '') => {
    const loadParams = {
      endPointName: 'addPromo',
      postData: [{ code: promoCode }],
      cartType: 'SUBSCRIPTION_OVERRIDE',
    }
    const response = await this.fetchResponse(loadParams)
    return response
  }

  getAllItemSubscriptions = async (subscriptionId = '') => {
    const loadParams = {
      endPointName: 'getAllItemSubscriptions',
      pathParams: `${subscriptionId}/summary`,
      queryParams: {
        showAll: true,
      },
    }
    const response = await this.fetchResponse(loadParams)
    if (this.isSuccessResponse(response)) {
      this.selectedSubscriptionGroupItemResponse = response
    } else {
      this.selectedSubscriptionGroupItemResponse = {}
    }
    return response
  }
  getSelectedSubscriptionGrandTotal = () => {
    const response = this.selectedSubscriptionGroupItemResponse || {}
    const cartValueGrandTotal = response?.cartValue?.grandTotal
    const grandTotal = cartValueGrandTotal ?? 0
    return grandTotal
  }

  getMaxGrandTotalFromResponse = () => {
    const response = this.selectedSubscriptionGroupItemResponse || {}
    const maxGrandTotal = response?.properties?.maxGrandTotal ?? 0
    return maxGrandTotal
  }

  createSubscriptionEMandate = async (subscriptionId = '', payload = {}) => {
    const queryParams = {
      isDeleteFromCart: true,
    }

    const cartType = cartContainer?.cartResponse?.cartType
    if (cartType) {
      queryParams.cartType = cartType
    }

    const loadParams = {
      endPointName: 'createSubscriptionEMandate',
      pathParams: `${subscriptionId}/eMandate`,
      queryParams,
      postData: payload,
    }

    const response = await this.fetchResponse(loadParams)

    if (!this.isSuccessResponse(response)) {
      const errorMessage =
        response?.message ||
        i18nTranslate(
          'subscription.eMandateFailure',
          'Unable to process eMandate request. Please try again.'
        )
      toastState.setToastMessage(errorMessage, false)
    }

    return response
  }

  updateSubscriptionEMandateStatus = async (
    subscriptionId = '',
    gpsOrderId = '',
    storeId = ''
  ) => {
    const queryParams = {
      isDeleteFromCart: true,
    }

    const cartType = cartContainer?.cartResponse?.cartType
    if (cartType) {
      queryParams.cartType = cartType
    }

    const loadParams = {
      endPointName: 'updateSubscriptionEMandateStatus',
      pathParams: `${subscriptionId}/status/${gpsOrderId}`,
      queryParams,
      postData: {
        storeId,
      },
    }

    const response = await this.fetchResponse(loadParams)
    if (!this.isSuccessResponse(response)) {
      const errorMessage =
        response?.message ||
        i18nTranslate(
          'subscription.eMandateStatusUpdateFailure',
          'Unable to update payment status. Please try again.'
        )
      toastState.setToastMessage(errorMessage, false)
    }

    return response
  }

  /**
   * Deletes a subscription item from the subscription dashboard.
   * Calls the DELETE subscription item API with the given subscriptionId and itemId.
   *
   * @param {string} subscriptionId - The subscription group ID
   * @param {number|string} itemId - The subscription item ID to delete
   * @returns {Promise} Response from the delete API call
   */
  deleteSubscriptionDashboardItem = async (
    subscriptionId = '',
    itemId = ''
  ) => {
    if (!subscriptionId || !itemId) {
      return null
    }

    const loadParams = {
      endPointName: 'deleteSubscriptionDashboardItem',
      pathParams: `${subscriptionId}/item`,
      queryParams: {
        itemId,
      },
    }

    const response = await this.fetchResponse(loadParams)
    if (!response?.data) {
      toastState.setToastMessage(
        i18nTranslate(
          'subscription.itemRemovedSuccess',
          'Subscription item removed successfully'
        ),
        true
      )
    } else {
      const errorMessage =
        response?.message ||
        i18nTranslate(
          'subscription.itemRemovedFailure',
          'Unable to remove subscription item. Please try again.'
        )
      toastState.setToastMessage(errorMessage, false)
    }

    return response
  }
}

const multipleSubscriptionContainer = new MultipleSubscriptionContainer()

export { multipleSubscriptionContainer }
export default multipleSubscriptionContainer
